'use client';
/* eslint-disable import/prefer-default-export */
import React from 'react';
import {
  SurgicalChecklistDTO,
} from '@/dtos';
import {
  formatDate,
  formatTwoDigitDate,
} from '@/utils/date';
import ChecklistStatus from '@/components/ChecklistStatus';
import {
  RiskLevel,
} from '@/components/PhaseStatus';
import Link from 'next/link';
import {
  Eye,
} from 'lucide-react';
import {
  VirtualTableColumnDef as ColumnDef,
} from '@/core/components/VirtualTable/types';
import Name from '@/components/Name';
import {
  getPatientAge,
} from '@/utils/misc';
import {
  useMedia,
} from '@/core/hooks';
import ToolTip from '@/core/components/Tooltip';
import {
  notEmptyString,
} from '@/utils/misc';
import {
  PHASE_LABELS,
  PhaseKey,
} from '@/constants/phases';
import PhaseStepper, {
  PhaseStep,
} from '@/components/PhaseStepper';
import {
  getPhaseKeys,
} from '@/constants/phases';
import keyBy from 'lodash/keyBy';
import {
  pluralize,
} from '@/utils/string';
import {
  getDisplayName,
} from '@/utils/names';
import {
  DEFAULT_EMPTY_VALUE,
} from '@/constants';
import {
  Table,
} from '@tanstack/react-table';

export const columns: ColumnDef<SurgicalChecklistDTO>[] = [
  {
    id: 'patient',
    header: 'Patient',
    columnTitle: (table: Table<SurgicalChecklistDTO>) => {
      const { totalPatients } = (table?.options?.meta as any) ?? {};
      const count = totalPatients === 0 ? '' : `(${totalPatients})`;
      return `${pluralize('Patient', totalPatients, false)} ${count}`;
    },
    meta: { autoSized: true },
    minSize: 270,
    cell: ({ row: { original: { appointment } } }) => {
      const { getMediaPathById } = useMedia();
      const patient = appointment?.patient;
      const patientName = getDisplayName(patient?.formattedNames, DEFAULT_EMPTY_VALUE);
      const { dateOfBirth, genderDescription } = appointment?.patient ?? {};
      const age = getPatientAge(dateOfBirth ?? '');
      const avatarSrc = getMediaPathById(patient?.patientId ?? 0) ?? undefined;

      return (
        <div className="min-w-0">
          <ToolTip title={patientName}>
            <span>
              <Name
                {...(patient?.formattedNames ?? {})}
                image={{ variant: 'circular', src: avatarSrc }}
                classes={{ label: 'text-sm font-semibold text-slate-900' }}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <div className="text-xs text-slate-600 whitespace-nowrap">{formatTwoDigitDate(dateOfBirth ?? '')}</div>
                    <span className="text-slate-400">•</span>
                    <div className="text-xs text-slate-600 whitespace-nowrap">{age}</div>
                  </div>
                  <span className="text-xs font-normal text-slate-600">{genderDescription}</span>
                </div>
              </Name>
            </span>
          </ToolTip>
        </div>
      );
    },
  },
  {
    id: 'procedure',
    header: 'Procedure',
    size: 100,
    minSize: 110,
    cell: ({ row: { original: { appointment } } }) => {
      const procedure = appointment?.reasonForVisit ?? appointment?.typeOfVisitDescription ?? DEFAULT_EMPTY_VALUE;
      return (
        <ToolTip
          title={<span className='text-center'>{procedure}</span>}
          hide={!notEmptyString(procedure)}>
          <div className="text-xs text-slate-700 whitespace-pre-wrap break-words line-clamp-3">
            {procedure}
          </div>
        </ToolTip>
      );
    },
  },
  {
    id: 'insurance',
    header: 'Insurance',
    minSize: 100,
    size: 200,
    cell: ({ row: { original: { appointment } } }) => (
      <div className="text-xs text-slate-700 whitespace-pre-wrap mr-4">{appointment?.insurance?.insuranceName ?? DEFAULT_EMPTY_VALUE}</div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    size: 160,
    minSize: 160,
    cell: ({ row: { original: { status } } }) => (
      <ChecklistStatus status={status} className="size-max" />
    ),
  },
  {
    id: 'lastUpdated',
    header: 'Last Updated',
    size: 120,
    minSize: 100,
    cell: ({ row: { original: { updatedOn } } }) => (
      <div className="text-xs text-slate-600">{formatDate(updatedOn ?? '')}</div>
    ),
  },
  {
    id: 'reviews',
    header: 'Reviews',
    size: 560,
    minSize: 450,
    multiLineHeader: true,
    cell: ({ row: { original: { surgicalChecklistId, checklistPhases } } }) => {
      const byType = new Map(
        Object.entries(keyBy(checklistPhases, 'phaseType')),
      );
      const orderedKeys = getPhaseKeys();
      const steps: PhaseStep[] = orderedKeys.map((key) => {
        const phase = byType.get(key);
        const risk = (phase?.riskLevel as RiskLevel) ?? 'Pending';
        return {
          key,
          label: PHASE_LABELS[key as PhaseKey] ?? key,
          risk,
        } as PhaseStep;
      });
      return (
        <PhaseStepper
          steps={steps}
          checklistId={surgicalChecklistId}
        />
      );
    },
  },
  {
    id: 'actions',
    header: '',
    size: 135,
    minSize: 135,
    className: 'justify-end border-l border-gray-200 !pl-5',
    cell: ({ row: { original: { surgicalChecklistId, checklistPhases } } }) => (
      <Link
        href={`/or-display?id=${encodeURIComponent(String(surgicalChecklistId))}`}
        className="min-w-max inline-flex items-center gap-2 px-2.5 py-2 rounded-md border border-gray-200 text-slate-900 text-xs font-medium hover:bg-slate-50">
        <Eye className="w-3.5 h-3.5" />
        <span>OR Display</span>
      </Link>
    ),
  },
];
